import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { ICON_OPTIONS } from './welcomeConstants';

// Site Area Modal
export function SiteAreaModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{modal.data?.id && !modal.data.id.startsWith('temp-') ? 'Edit Site Area' : 'Add Site Area'}</DialogTitle>
                    <DialogDescription>Configure the site areas displayed in the Welcome Guide.</DialogDescription>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Title *</Label>
                            <Input value={modal.data.title} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))} placeholder="e.g., The Clubhouse" data-testid="site-area-title-input" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={modal.data.description} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))} placeholder="Brief description of the area..." rows={3} data-testid="site-area-desc-input" />
                        </div>
                        <div>
                            <Label>Icon</Label>
                            <select value={modal.data.icon} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, icon: e.target.value } }))} className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1" data-testid="site-area-icon-select">
                                {ICON_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                            </select>
                        </div>
                        <div>
                            <Label>Display Order</Label>
                            <Input type="number" value={modal.data.display_order} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, display_order: parseInt(e.target.value) || 0 } }))} min={0} data-testid="site-area-order-input" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-teal-600 hover:bg-teal-700" data-testid="save-site-area-btn">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Contact Modal
export function ContactModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{modal.data?.id && !modal.data.id.startsWith('temp-') ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
                    <DialogDescription>Add key contact information for residents.</DialogDescription>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Role / Department *</Label>
                            <Input value={modal.data.role} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, role: e.target.value } }))} placeholder="e.g., Reception / Concierge" data-testid="contact-role-input" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={modal.data.description} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))} placeholder="What they help with..." rows={2} data-testid="contact-desc-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Phone</Label>
                                <Input value={modal.data.phone} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, phone: e.target.value } }))} placeholder="01234 567890" data-testid="contact-phone-input" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={modal.data.email || ''} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, email: e.target.value } }))} placeholder="reception@example.com" data-testid="contact-email-input" />
                            </div>
                        </div>
                        <div>
                            <Label>Availability</Label>
                            <Input value={modal.data.availability} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, availability: e.target.value } }))} placeholder="e.g., Mon-Fri, 9am-5pm" data-testid="contact-availability-input" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-teal-600 hover:bg-teal-700" data-testid="save-contact-btn">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Hours Modal
export function HoursModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{modal.data?.id && !modal.data.id.startsWith('temp-') ? 'Edit Opening Hours' : 'Add Opening Hours'}</DialogTitle>
                    <DialogDescription>Configure venue operating hours.</DialogDescription>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Venue Name *</Label>
                            <Input value={modal.data.venue} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, venue: e.target.value } }))} placeholder="e.g., Restaurant" data-testid="hours-venue-input" />
                        </div>
                        <div>
                            <Label>Weekday Hours</Label>
                            <Input value={modal.data.weekday_hours} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, weekday_hours: e.target.value } }))} placeholder="e.g., 7:30am - 9:00pm" data-testid="hours-weekday-input" />
                        </div>
                        <div>
                            <Label>Weekend Hours</Label>
                            <Input value={modal.data.weekend_hours} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, weekend_hours: e.target.value } }))} placeholder="e.g., 8:00am - 9:00pm" data-testid="hours-weekend-input" />
                        </div>
                        <div>
                            <Label>Display Order</Label>
                            <Input type="number" value={modal.data.display_order} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, display_order: parseInt(e.target.value) || 0 } }))} min={0} data-testid="hours-order-input" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-teal-600 hover:bg-teal-700" data-testid="save-hours-btn">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Wellness Modal
export function WellnessModal({ modal, setModal, onSave }) {
    return (
        <Dialog open={modal.open} onOpenChange={(open) => !open && setModal({ open: false, data: null })}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{modal.data?.id && !modal.data.id.startsWith('temp-') ? 'Edit Wellness Service' : 'Add Wellness Service'}</DialogTitle>
                    <DialogDescription>Configure wellness services available to residents.</DialogDescription>
                </DialogHeader>
                {modal.data && (
                    <div className="space-y-4">
                        <div>
                            <Label>Service Title *</Label>
                            <Input value={modal.data.title} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))} placeholder="e.g., Physiotherapy" data-testid="wellness-title-input" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={modal.data.description} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))} placeholder="Brief description of the service..." rows={2} data-testid="wellness-desc-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Price</Label>
                                <Input value={modal.data.price || ''} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, price: e.target.value } }))} placeholder="e.g., £45" data-testid="wellness-price-input" />
                            </div>
                            <div>
                                <Label>Duration (mins)</Label>
                                <Input type="number" value={modal.data.duration_minutes || ''} onChange={(e) => setModal(prev => ({ ...prev, data: { ...prev.data, duration_minutes: parseInt(e.target.value) || null } }))} placeholder="60" data-testid="wellness-duration-input" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={modal.data.bookable} onCheckedChange={(checked) => setModal(prev => ({ ...prev, data: { ...prev.data, bookable: checked } }))} data-testid="wellness-bookable-toggle" />
                            <Label>Bookable through the app</Label>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setModal({ open: false, data: null })}>Cancel</Button>
                    <Button onClick={onSave} className="bg-teal-600 hover:bg-teal-700" data-testid="save-wellness-btn">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Delete Confirmation Modal
export function DeleteConfirmModal({ deleteConfirm, setDeleteConfirm, onConfirm }) {
    return (
        <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: '', id: null, name: '' })}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogDescription>This action cannot be undone.</DialogDescription>
                </DialogHeader>
                <p className="text-muted-foreground">Are you sure you want to delete "{deleteConfirm.name}"?</p>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })}>Cancel</Button>
                    <Button variant="destructive" onClick={onConfirm} data-testid="confirm-delete-btn">Delete</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
